import { AlertTriangle, CheckCircle2, Clock3 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBusinessAuth } from '../../context/BusinessAuthContext';
import { allowedActions, orderAttention } from '../../utils/orderAttention';

const TONES = Object.freeze({
  action: {
    Icon: AlertTriangle, heading: 'Needs attention',
    box: 'border-warning bg-warning-subtle text-warning',
    primary: 'bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-active',
    secondary: 'border border-warning hover:bg-hover',
  },
  waiting: {
    Icon: Clock3, heading: 'Waiting',
    box: 'border-neutral-200 bg-neutral-50 text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100',
    primary: '', secondary: 'border border-neutral-300 hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800',
  },
  settled: {
    Icon: CheckCircle2, heading: 'Nothing more to do',
    box: 'border-neutral-200 bg-neutral-50 text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100',
    primary: '', secondary: 'border border-neutral-300 hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800',
  },
});

// One place that says why an order left the normal path and where it is
// resolved. Each link goes to an existing page; the server still decides
// whether the person may act there.
export default function OrderAttentionPanel({ order, attention: given, className = '' }) {
  const auth = useBusinessAuth();
  const attention = given === undefined ? orderAttention(order) : given;
  if (!attention) return null;
  const owner = String(auth?.role || '').toUpperCase() === 'OWNER';
  const can = (permission) => owner || Boolean(auth?.hasPermission?.(permission));
  const actions = allowedActions(attention, can);
  const tone = TONES[attention.kind] || TONES.action;
  const { Icon } = tone;
  const noWayForward = attention.kind === 'action' && !actions.some((action) => action.primary);

  return (
    <section aria-labelledby="order-attention-title" data-testid="order-attention" data-kind={attention.kind}
      className={`flex gap-3 rounded-xl border p-4 text-sm ${tone.box} ${className}`}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] opacity-80">{tone.heading}</p>
        <h2 id="order-attention-title" className="mt-0.5 text-base font-semibold">{attention.title}</h2>
        <p className="mt-1 max-w-3xl leading-6">{attention.message}</p>
        {attention.reason ? <p className="mt-2 max-w-3xl break-words">Reason given: <q>{attention.reason}</q></p> : null}
        {attention.note ? <p className="mt-2 max-w-3xl leading-6">{attention.note}</p> : null}
        {noWayForward && attention.unavailable ? <p className="mt-2 max-w-3xl font-medium">{attention.unavailable}</p> : null}
        {actions.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {actions.map((action) => (
              <Link key={action.to} to={action.to}
                className={`inline-flex min-h-11 items-center rounded-lg px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${action.primary && tone.primary ? tone.primary : tone.secondary}`}>
                {action.label}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
